<script setup lang="ts">
  import { onMounted, provide, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { StuutPortalApi } from '@/api';
  import { Invoice, PortalProfile } from '@stuut/api/models';
  import Button from '@ui/components/button/Button.vue';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { ToastError } from '@ui/components/sonner';
  // import { RoutedTabsTrigger, Tabs, TabsList } from '@ui/components/tabs';
  import { PortalRoutes } from './routes';
  import Login from '@/components/Login.vue';
  import MakePaymentButton from '@/components/MakePaymentButton.vue';
  import OrgSupportEmail from '@/components/OrgSupportEmail.vue';

  definePage({ name: 'portal' });

  const route = useRoute();
  const loading = ref(true);
  const router = useRouter();

  /** @TODO include customer_support_email in anon profile response
   *  - isn't be available without login
   *  - so on login page, it will show default support email
   *    rather than the profile's `customer_support_email`
   *  - potential security / exfiltration risk since
   *    it would technically be available to anyone
   *    with access to this page?
   */
  const profile = ref<PortalProfile>();
  const selectedInvoices = ref<Invoice[]>([]);

  const broadcastChannel = new BroadcastChannel('login_channel');

  broadcastChannel.onmessage = (event) => {
    if (event.data.type === 'logout') {
      location.reload();
    }
  };

  async function consumeMagicLinkToken() {
    try {
      const data = { token: route.query.token };
      const response = await StuutPortalApi.post(`/portal/auth/token`, {
        data,
      });
      localStorage.setItem('savedMagicLinkEmail', response.data.email);
      broadcastChannel.postMessage({
        type: 'login',
        partnerId: route.params.partnerId,
      });
    } catch (e) {
      throw new ToastError('Your Magic Link has expired. Please log in again.');
    } finally {
      loading.value = false;

      // remove token from query string
      const query = { ...route.query };
      delete query.token;
      router.replace({ query });
    }
  }

  async function logout() {
    await StuutPortalApi.post('/portal/auth/logout');
    broadcastChannel.postMessage({ type: 'logout' });
    location.reload();
  }

  function handleSelectionChange(invoices: Invoice[]) {
    selectedInvoices.value = invoices;
  }

  // Provide profile and handlers to child routes
  provide('portalProfile', profile);
  provide('onInvoiceSelectionChange', handleSelectionChange);

  const tabs = [
    {
      icon: 'far fa-file-invoice',
      route: PortalRoutes.INVOICES,
      label: 'Invoices',
    },
    {
      icon: 'far fa-credit-card',
      route: PortalRoutes.PAYMENTS,
      label: 'Payments',
    },
    {
      icon: 'far fa-receipt',
      route: PortalRoutes.CREDIT_MEMOS,
      label: 'Credit Memos',
    },
  ];

  onMounted(async () => {
    if (route.query.token) {
      // will throw an error if token is not valid
      await consumeMagicLinkToken();
    }

    try {
      profile.value = await StuutPortalApi.portalProfiles.retrieve();
    } finally {
      loading.value = false;
    }
  });
</script>

<template>
  <div class="flex justify-center w-full">
    <div
      class="flex flex-col w-full max-w-6xl min-h-screen p-4 mx-auto bg-white"
    >
      <LoadingSpinner v-if="loading" class="text-2xl flex justify-center" />
      <template v-else-if="profile">
        <div class="flex justify-between items-center sm:my-4 gap-4">
          <h2 class="py-2 text-h3 text-left break-words text-ellipsis">
            {{ profile.partner_name }} Payment Portal
          </h2>
          <div class="flex items-center gap-3 w-1/2 xs:w-1/3 justify-end">
            <MakePaymentButton
              :selected-invoices="selectedInvoices"
              :organization-slug="route.params.organizationSlug as string"
              :payments-enabled="profile.payments_enabled"
            />
            <div class="text-right text-neutral-400 text-xs text-ellipsis">
              <Button class="px-2" variant="outline" @click="logout">
                <i class="fas fa-sign-out-alt mx-1 cursor-pointer text-xs" />
              </Button>
            </div>
          </div>
        </div>

        <!--
        @TODO portal phase 2 -> add tabbed nav

        <Tabs :model-value="route.name as string" class="bg-white sticky top-0">
          <TabsList class="w-full">
            <RoutedTabsTrigger
              v-for="tab in tabs"
              :key="tab.route"
              :active="route.name === tab.route"
              :routed-tab-item="tab"
            />
          </TabsList>
        </Tabs> -->

        <div class="flex flex-1 pt-4 !overflow-visible overflow-y-auto">
          <router-view class="h-full w-full" />
        </div>
      </template>
      <Login v-else :partner-id="route.params.partnerId" />

      <OrgSupportEmail
        v-if="!loading"
        :email="profile?.customer_support_email"
        class="mt-2"
      />
    </div>
  </div>
</template>

<style></style>
